/**
 * useDepartmentExport Hook
 * Custom hook for exporting departments to Excel
 */

import { useState, useCallback } from 'react';
import { departmentService } from '../services/departmentService';
import { DepartmentFilters } from '../types/department';
import { useSnackbar } from 'notistack';

interface UseDepartmentExportResult {
  exporting: boolean;
  exportDepartments: (filters?: Partial<DepartmentFilters>, filename?: string) => Promise<void>;
}

export const useDepartmentExport = (): UseDepartmentExportResult => {
  const { enqueueSnackbar } = useSnackbar();

  const [exporting, setExporting] = useState<boolean>(false);

  const exportDepartments = useCallback(async (
    filters?: Partial<DepartmentFilters>,
    filename = 'setores.xlsx'
  ): Promise<void> => {
    try {
      setExporting(true);

      const blob = await departmentService.exportDepartments(filters);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Setores exportados com sucesso', { variant: 'success' });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao exportar setores';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setExporting(false);
    }
  }, [enqueueSnackbar]);

  return {
    exporting,
    exportDepartments
  };
};
